import React from 'react';

function Terms() {
  return (
    <div className="legal-page">
      <h1>Terms of Service</h1>
      <p className="last-updated">Last updated: January 2025</p>

      <section>
        <h2>Agreement to Terms</h2>
        <p>
          By downloading, installing, or using NZBEE ("the App"), you agree to be bound by these
          Terms of Service. If you do not agree to these terms, do not use the App.
        </p>
      </section>

      <section>
        <h2>Description of Service</h2>
        <p>
          NZBEE is a mobile application that provides a unified interface for searching NZB files and
          managing self-hosted download servers including SabNZBD and NZBGet. The App acts as a client
          that connects to third-party search services and servers you own or have authorization to access.
        </p>
      </section>

      <section>
        <h2>User Responsibilities</h2>
        <p>You are responsible for:</p>
        <ul>
          <li>Ensuring you have proper authorization to access and control the services you connect to through NZBEE</li>
          <li>Maintaining the security of your credentials and server configurations</li>
          <li>Complying with all applicable laws regarding the content you access, search for, or download through the App</li>
          <li>The security and configuration of your own servers and services</li>
          <li>Ensuring you only search for and download content that you are legally authorized to access</li>
          <li>Complying with all applicable copyright laws and regulations in your jurisdiction</li>
        </ul>
      </section>

      <section>
        <h2>Acceptable Use</h2>
        <p>You agree not to use NZBEE to:</p>
        <ul>
          <li>Access servers or services without proper authorization</li>
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe on the intellectual property rights of others</li>
          <li>Download copyrighted material without proper authorization</li>
          <li>Distribute malware or engage in any malicious activity</li>
          <li>Access or use third-party search services in violation of their terms of service</li>
        </ul>
      </section>

      <section>
        <h2>Copyright and Legal Content</h2>
        <p>
          <strong>IMPORTANT:</strong> NZBEE is a technical tool that does not host, store, or distribute
          any content. Users are solely responsible for ensuring they have the legal right to access and
          download any content. We do not endorse, encourage, or condone the downloading of copyrighted
          material without proper authorization.
        </p>
        <p>
          By using NZBEE, you acknowledge and agree that you will only use the App to search for and download
          content that you are legally authorized to access, and that you will comply with all applicable
          copyright laws.
        </p>
      </section>

      <section>
        <h2>Intellectual Property</h2>
        <p>
          NZBEE and its original content, features, and functionality are owned by NZBEE and are protected
          by international copyright, trademark, and other intellectual property laws.
        </p>
      </section>

      <section>
        <h2>Third-Party Services</h2>
        <p>
          NZBEE integrates with third-party services (including but not limited to NZB search services,
          SabNZBD, and NZBGet) that have their own terms of service. Your use of these services through
          NZBEE is subject to their respective terms. We are not responsible for the availability,
          functionality, content, or legality of these third-party services.
        </p>
        <p>
          You are responsible for:
        </p>
        <ul>
          <li>Obtaining your own API access from third-party services</li>
          <li>Reviewing and agreeing to the third-party service's terms of use and privacy policies</li>
          <li>Ensuring your use of third-party services complies with their terms and applicable laws</li>
          <li>Understanding that third-party services may index content of varying legality</li>
        </ul>
        <p>
          <strong>NZBEE is not affiliated with, endorsed by, or responsible for any third-party search
          services or download server software.</strong>
        </p>
      </section>

      <section>
        <h2>Disclaimer of Warranties</h2>
        <p>
          NZBEE is provided "as is" and "as available" without warranties of any kind, either express or
          implied. We do not warrant that the App will be uninterrupted or error-free.
        </p>
        <p>
          We make no warranties or representations about:
        </p>
        <ul>
          <li>The accuracy or completeness of search results from third-party services</li>
          <li>The availability or functionality of third-party servers or services</li>
          <li>The legality of content accessible through third-party services</li>
          <li>The security of your self-hosted servers</li>
        </ul>
      </section>

      <section>
        <h2>Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, NZBEE and its developers shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages, including but not limited to:
        </p>
        <ul>
          <li>Loss of data or loss of profits</li>
          <li>Damages arising from your use of the App or inability to use the App</li>
          <li>Any legal issues arising from your use of third-party services</li>
          <li>Any copyright infringement or other legal violations by users</li>
          <li>Any damages related to content accessed, downloaded, or managed through the App</li>
        </ul>
        <p>
          The developers of NZBEE do not control, monitor, or have knowledge of what users search for or
          download. We do not host, store, transmit, or distribute any content accessed through the App.
        </p>
      </section>

      <section>
        <h2>Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless NZBEE and its developers from any claims, damages,
          losses, or expenses (including legal fees) arising from:
        </p>
        <ul>
          <li>Your use of the App</li>
          <li>Your violation of these terms</li>
          <li>Your violation of any applicable laws or regulations</li>
          <li>Your infringement of any intellectual property or other rights</li>
          <li>Your use of third-party services through the App</li>
        </ul>
      </section>

      <section>
        <h2>iCloud Sync</h2>
        <p>
          If you enable optional iCloud sync, your server settings and credentials will be synced via
          Apple's iCloud services. This feature is optional and disabled by default. When enabled, you
          acknowledge that Apple's terms and privacy policies apply to the synced data. NZBEE is not
          responsible for the security or availability of iCloud services.
        </p>
      </section>

      <section>
        <h2>Modifications to Terms</h2>
        <p>
          We reserve the right to modify these terms at any time. We will notify users of any material
          changes by updating the "Last updated" date. Your continued use of the App after changes
          constitutes acceptance of the new terms.
        </p>
      </section>

      <section>
        <h2>Governing Law</h2>
        <p>
          These terms shall be governed by and construed in accordance with applicable laws, without
          regard to conflict of law principles.
        </p>
      </section>

      <section>
        <h2>Entire Agreement</h2>
        <p>
          These Terms of Service, together with our Privacy Policy, constitute the entire agreement
          between you and NZBEE regarding your use of the App.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>For questions about these Terms of Service, please contact us at:</p>
        <p><a href="mailto:contact@nzbee.net">contact@nzbee.net</a></p>
      </section>

      <div className="disclaimer">
        <p>
          <strong>Disclaimer:</strong> NZBEE is an independent application and is not affiliated with,
          endorsed by, or associated with any third-party search services, download server software, or
          content providers. All third-party services and software are independent projects with their
          own terms, policies, and operators.
        </p>
      </div>
    </div>
  );
}

export default Terms;
